import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import type { EmailProviderData, MappedLeadData } from '../../types';
import { VerifiedBadge, ChannelVerifiedBadge } from './VerifiedBadge';

/** Lazily-created portal root so tooltips escape overflow-clipped containers. */
const getIntTooltipPortal = (): HTMLElement => {
  let root = document.getElementById('channel-intel-tooltip-root');
  if (!root) {
    root = document.createElement('div');
    root.id = 'channel-intel-tooltip-root';
    document.body.appendChild(root);
  }
  return root;
};

/**
 * Channel Intelligence atoms
 *
 * Shared presentation for the per-channel verification process. Every channel
 * we discover for a lead is validated before outreach — these components make
 * that visible: a quality-colored channel icon at rest, and a rich hover card
 * with the full verification detail (scoring, provider, deliverability flags).
 *
 * Reused by: lead Overview tab, campaign list ChannelIcons, ISR contacts,
 * and the ISR Sequence tab channel-verification strip.
 */

// ── Email quality model ──────────────────────────────────────────────────────

export type EmailQualityTone = 'verified' | 'valid' | 'caution' | 'risk';

export interface EmailQualityInfo {
  tone: EmailQualityTone;
  /** Iconify name summarizing the quality (used in headers and badges). */
  icon: string;
  /** Hex color for the channel icon / badge text. */
  color: string;
  /** Short badge label, e.g. "Verified · 92/100". */
  label: string;
  /** One-line explanation shown as the intel card subtitle. */
  detail: string;
}

/**
 * Derive the verification quality of an email from its provider validation
 * data. Priority: spam trap > unsafe > catch-all > role account > score bands.
 * Returns null when there is nothing meaningful to show (no data / score 0).
 */
export const describeEmailQuality = (
  data: EmailProviderData | null | undefined,
): EmailQualityInfo | null => {
  if (!data) return null;
  // Disposable email — ghost icon, its own tone.
  if (data.is_disposable) {
    return { tone: 'risk', icon: 'solar:ghost-smile-bold-duotone', color: '#EF4444', label: 'Disposable', detail: 'This is a throwaway email address — it will not reach a real inbox.' };
  }
  // Spam trap always wins — never contact.
  if (data.is_spamtrap) {
    return { tone: 'risk', icon: 'solar:danger-triangle-bold-duotone', color: '#EF4444', label: 'Spam trap', detail: 'This address is a spam trap — never send to it.' };
  }
  // is_safe_to_send is too aggressive (catches Barracuda, Proofpoint, etc.
  // that are actually deliverable) — only trust the score.
  if (data.score >= 80) {
    return { tone: 'verified', icon: 'solar:check-circle-bold-duotone', color: '#10B981', label: `Verified · ${data.score}/100`, detail: 'Passed all verification checks. Safe to send.' };
  }
  if (data.score >= 50) {
    if (data.is_catch_all) {
      return { tone: 'caution', icon: 'solar:info-circle-bold-duotone', color: '#F59E0B', label: `Catch-all · ${data.score}/100`, detail: 'The domain accepts every recipient — delivery may still bounce.' };
    }
    if (data.is_role_account) {
      return { tone: 'caution', icon: 'solar:info-circle-bold-duotone', color: '#F59E0B', label: `Role account · ${data.score}/100`, detail: 'Team inbox (info@, sales@…) rather than a personal address.' };
    }
    return { tone: 'valid', icon: 'solar:check-circle-bold-duotone', color: '#10B981', label: `Verified · ${data.score}/100`, detail: 'Deliverable email — passed verification.' };
  }
  if (data.score > 0) {
    return { tone: 'risk', icon: 'solar:danger-triangle-bold-duotone', color: '#EF4444', label: `Risky · ${data.score}/100`, detail: 'Low quality — high risk of bounce.' };
  }
  // Score is 0 or missing — fall back to the flags for any signal.
  if (data.is_catch_all) {
    return { tone: 'caution', icon: 'solar:info-circle-bold-duotone', color: '#F59E0B', label: 'Catch-all domain', detail: 'The domain accepts every recipient — delivery may still bounce.' };
  }
  if (data.is_role_account) {
    return { tone: 'caution', icon: 'solar:info-circle-bold-duotone', color: '#F59E0B', label: 'Role account', detail: 'Team inbox (info@, sales@…) rather than a personal address.' };
  }
  if (!data.is_safe_to_send) {
    return { tone: 'caution', icon: 'solar:info-circle-bold-duotone', color: '#F59E0B', label: 'Unverified', detail: 'Email provider flagged by Barracuda/Proofpoint but may still be deliverable.' };
  }
  return null;
};

// ── Tooltip wrapper ──────────────────────────────────────────────────────────

interface ChannelIntelTooltipProps {
  /** Rich card content; pass null to render the children with no tooltip. */
  content: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

/**
 * Portal-based tooltip that renders above the trigger at `position: fixed`
 * so it always escapes overflow-clipped containers. Uses visibility + opacity
 * (not conditional rendering) so the DOM node is always present and the
 * position is recalculated before it becomes visible.
 */
export const ChannelIntelTooltip: React.FC<ChannelIntelTooltipProps> = ({ content, children, className = '' }) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const recalc = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const half = 144 + 8;
    const left = Math.min(Math.max(rect.left + rect.width / 2, half), window.innerWidth - half);
    setPos({ left, top: rect.top - 8 });
  };

  const enter = () => {
    if (timer.current) clearTimeout(timer.current);
    recalc();
    setShow(true);
  };
  const leave = () => {
    timer.current = setTimeout(() => setShow(false), 150);
  };

  if (!content) return <>{children}</>;

  return (
    <span
      ref={ref}
      className={`inline-flex ${className}`}
      onMouseEnter={enter}
      onMouseLeave={leave}
    >
      {children}
      {createPortal(
        <div
          style={{
            left: pos.left,
            top: pos.top,
            transform: 'translateX(-50%) translateY(-100%)',
            visibility: show ? 'visible' : 'hidden',
            opacity: show ? 1 : 0,
          }}
          className="fixed z-[9999] w-72 rounded-2xl border border-white/10 bg-[#0B0B0D]/96 p-3.5 shadow-[0_20px_60px_rgba(0,0,0,0.5)] backdrop-blur-xl transition-all duration-100"
          role="tooltip"
          onMouseEnter={enter}
          onMouseLeave={leave}
        >
          {content}
        </div>,
        getIntTooltipPortal(),
      )}
    </span>
  );
};

// ── Email intel card (tooltip content) ───────────────────────────────────────

const IntelRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <>
    <dt className="text-white/40">{label}</dt>
    <dd className="min-w-0 break-words text-white/80">{children}</dd>
  </>
);

const IntelFlag: React.FC<{ tone: 'good' | 'warn' | 'risk'; children: React.ReactNode }> = ({ tone, children }) => {
  const cls = tone === 'good' ? 'text-emerald-300/90' : tone === 'warn' ? 'text-amber-300/90' : 'text-rose-300/90';
  const icon = tone === 'good' ? 'bx:check-circle' : tone === 'warn' ? 'bx:info-circle' : 'bx:x-circle';
  return (
    <li className={`flex items-start gap-1.5 ${cls}`}>
      <Icon icon={icon} className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="leading-5">{children}</span>
    </li>
  );
};

/** Full email verification card — the hover content for the email intel icon. */
export const EmailIntelCard: React.FC<{
  email: string;
  data: EmailProviderData;
  quality: EmailQualityInfo;
}> = ({ email, data, quality }) => (
  <div>
    <div className="flex items-center gap-2">
      <Icon icon={quality.icon} className="h-4 w-4 shrink-0" style={{ color: quality.color }} aria-hidden="true" />
      <span className="text-[12px] font-semibold" style={{ color: quality.color }}>{quality.label}</span>
    </div>
    <p className="mt-1 break-words text-[12px] font-medium text-white">{email}</p>
    <p className="mt-0.5 text-[11px] leading-4 text-white/45">{quality.detail}</p>

    {/* Score bar */}
    <div className="mt-2.5 flex items-center gap-2" aria-hidden="true">
      <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
        <div className="h-full rounded-full" style={{ width: `${Math.min(Math.max(data.score, 0), 100)}%`, backgroundColor: quality.color }} />
      </div>
      <span className="text-[10px] font-semibold tabular-nums text-white/55">{data.score}/100</span>
    </div>

    <dl className="mt-2.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[11px] leading-4">
      {data.email_provider && <IntelRow label="Provider">{data.email_provider}</IntelRow>}
      {data.domain && <IntelRow label="Domain">{data.domain}</IntelRow>}
      {data.status && <IntelRow label="Status"><span className="capitalize">{data.status.replace(/_/g, ' ')}</span></IntelRow>}
    </dl>

    <ul className="mt-2 space-y-0.5 text-[11px]">
      {data.score >= 50 && <IntelFlag tone="good">Safe to send</IntelFlag>}
      {data.can_connect_smtp && <IntelFlag tone="good">SMTP verified — mailbox exists</IntelFlag>}
      {data.is_valid_syntax && <IntelFlag tone="good">Valid syntax</IntelFlag>}
      {data.is_catch_all && <IntelFlag tone="warn">Catch-all domain — may bounce</IntelFlag>}
      {data.is_role_account && <IntelFlag tone="warn">Role account (info@, sales@…)</IntelFlag>}
      {data.has_inbox_full && <IntelFlag tone="warn">Inbox full</IntelFlag>}
      {data.is_free_email && <IntelFlag tone="warn">Free email provider</IntelFlag>}
      {data.is_disposable && <IntelFlag tone="risk">Disposable address</IntelFlag>}
      {!data.is_safe_to_send && data.score < 50 && <IntelFlag tone="warn">Marked unsafe by provider filter (Barracuda/Proofpoint) — score-based decision</IntelFlag>}
      {data.is_spamtrap && <IntelFlag tone="risk">Spam trap detected</IntelFlag>}
    </ul>
  </div>
);

// ── Email intel icon (the reusable trigger) ──────────────────────────────────

interface EmailIntelBadgeProps {
  email: string | null | undefined;
  data: EmailProviderData | null | undefined;
}

/**
 * "VERIFIED" pill for email quality — the email variant of the reusable
 * VerifiedBadge atom. Icon + verdict word on the pill; the full verification
 * card (score, provider, flags) on hover/click.
 */
export const EmailIntelBadge: React.FC<EmailIntelBadgeProps> = ({ email, data }) => {
  const quality = describeEmailQuality(data);
  const address = email || data?.email || '';
  if (!quality || !address) return null;

  const tone: 'verified' | 'caution' | 'risk' =
    quality.tone === 'risk' ? 'risk'
    : quality.tone === 'caution' ? 'caution'
    : 'verified';

  return (
    <VerifiedBadge
      icon={quality.icon}
      label={quality.tone === 'risk' ? 'RISKY' : 'VERIFIED'}
      tone={tone}
      ariaLabel={`Email verification — ${quality.label}. Click for details.`}
    >
      <EmailIntelCard email={address} data={data!} quality={quality} />
    </VerifiedBadge>
  );
};

// ── Sequence tab channel-verification strip ─────────────────────────────────

/** Map a sequence channel key to the matching lvl2 `_enriched` timestamp. */
const sequenceEnrichedKey = (channel: string): keyof typeof ENRICHED_LOOKUP | null => {
  switch (channel) {
    case 'email': return 'email';
    case 'whatsapp': return 'whatsapp';
    case 'phone': return 'phone';
    // SMS is MOBILE-ONLY — landlines/company phones cannot receive SMS.
    case 'sms': return 'mobile';
    case 'linkedin': return 'linkedin';
    default: return null;
  }
};

const ENRICHED_LOOKUP = {
  email: (lead: MappedLeadData) => lead.channelEnrichedAt?.email,
  whatsapp: (lead: MappedLeadData) => lead.channelEnrichedAt?.whatsapp,
  phone: (lead: MappedLeadData) => lead.channelEnrichedAt?.mobile || lead.channelEnrichedAt?.phone,
  mobile: (lead: MappedLeadData) => lead.channelEnrichedAt?.mobile,
  linkedin: (lead: MappedLeadData) => lead.channelEnrichedAt?.linkedin,
};

/** The channel value shown in the strip, per sequence channel. */
const sequenceChannelValue = (lead: MappedLeadData, channel: string): string | null => {
  switch (channel) {
    case 'email': return lead.email || null;
    case 'whatsapp': return lead.leadWhatsapp || lead.companyWhatsapp || null;
    // SMS is MOBILE-ONLY — never fall back to landline/company phone.
    case 'sms': return lead.mobile || null;
    case 'phone': return lead.mobile || lead.phone || lead.companyPhone || null;
    case 'linkedin': return lead.leadLinkedin || lead.companyLinkedin || null;
    default: return null;
  }
};

/** "Personal" / "Business" — which side of the lead the channel came from. */
const sequenceChannelScope = (lead: MappedLeadData, channel: string): string | null => {
  switch (channel) {
    case 'whatsapp': return lead.leadWhatsapp ? 'Personal' : lead.companyWhatsapp ? 'Business' : null;
    // SMS is mobile-only, so it's always the Personal mobile.
    case 'sms': return lead.mobile ? 'Personal' : null;
    case 'phone': return lead.mobile ? 'Personal' : lead.phone ? 'Business' : lead.companyPhone ? 'Business' : null;
    case 'linkedin': return lead.leadLinkedin ? 'Personal' : lead.companyLinkedin ? 'Business' : null;
    default: return null;
  }
};

/**
 * Short, at-a-glance insight for the ACTIVE sequence channel — the info a rep
 * needs before sending, shown inline so no hover is required.
 * Email: the quality verdict + score + the key flag. Others: verified date.
 */
const sequenceChannelInsight = (lead: MappedLeadData, channel: string): { text: string; color: string; icon: string } | null => {
  if (channel === 'email') {
    const quality = describeEmailQuality(lead.emailProviderData);
    if (!quality) return null;
    // One-line essence: verdict + the single most relevant flag.
    const flag = lead.emailProviderData?.is_catch_all ? 'catch-all domain'
      : lead.emailProviderData?.is_role_account ? 'role account'
      : lead.emailProviderData?.is_free_email ? 'free provider'
      : lead.emailProviderData?.can_connect_smtp ? 'SMTP verified'
      : null;
    return {
      text: flag ? `${quality.label} · ${flag}` : quality.label,
      color: quality.color,
      icon: quality.icon,
    };
  }
  const key = sequenceEnrichedKey(channel);
  const enrichedAt = key ? ENRICHED_LOOKUP[key](lead) : null;
  if (!enrichedAt) return null;
  const when = new Date(enrichedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return { text: `Verified by Brainiacs · ${when}`, color: '#10B981', icon: 'solar:check-circle-bold-duotone' };
};

/**
 * Channel verification strip for the ISR Sequence tab — shows ONLY the active
 * channel's verification: a compact one-liner with the value, scope, VERIFIED
 * pill, and the key insight inline (no hover needed to see what matters).
 * Keeps a small footprint; the message sequence remains the star.
 */
export const SequenceChannelIntel: React.FC<{ lead: MappedLeadData; activeChannel: string }> = ({ lead, activeChannel }) => {
  const value = sequenceChannelValue(lead, activeChannel);
  const scope = sequenceChannelScope(lead, activeChannel);
  const insight = sequenceChannelInsight(lead, activeChannel);

  if (!value && !insight) return null;

  const meta = SEQUENCE_CHANNEL_ICONS[activeChannel] || { icon: 'bx:message-rounded', color: '#A5B4FC' };

  return (
    <section
      aria-label="Channel verification"
      className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-2xl border border-white/[0.07] bg-white/[0.02] px-4 py-2.5"
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/35">
        Channel verification
      </span>
      <span className="inline-flex min-w-0 items-center gap-1.5 text-[12px]">
        <Icon icon={meta.icon} className="h-3.5 w-3.5 shrink-0" style={{ color: meta.color }} aria-hidden="true" />
        <span className="min-w-0 truncate font-medium text-white/80">{value}</span>
        {scope && (
          <span className="shrink-0 rounded bg-white/[0.07] px-1 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-white/45">
            {scope}
          </span>
        )}
      </span>
      {activeChannel === 'email' ? (
        <EmailIntelBadge email={lead.email} data={lead.emailProviderData} />
      ) : (
        <ChannelVerifiedBadge
          // SMS is mobile-only — show the mobile variant, and only when a
          // mobile number actually exists (ChannelVerifiedBadge hides itself
          // otherwise since mobile_enriched would be absent).
          channel={activeChannel === 'sms' ? 'mobile' : activeChannel === 'phone' ? (lead.mobile ? 'mobile' : 'phone') : activeChannel as 'whatsapp' | 'linkedin'}
          enrichedAt={sequenceEnrichedKey(activeChannel) ? ENRICHED_LOOKUP[sequenceEnrichedKey(activeChannel)!](lead) : null}
          value={value}
        />
      )}
      {insight && (
        <span className="inline-flex min-w-0 items-center gap-1.5 text-[11px]">
          <Icon icon={insight.icon} className="h-3.5 w-3.5 shrink-0" style={{ color: insight.color }} aria-hidden="true" />
          <span className="truncate font-medium" style={{ color: insight.color }}>{insight.text}</span>
        </span>
      )}
    </section>
  );
};

/** Brand icons + colors for the sequence channels in the strip. */
const SEQUENCE_CHANNEL_ICONS: Record<string, { icon: string; color: string }> = {
  email: { icon: 'solar:letter-bold-duotone', color: '#A5B4FC' },
  whatsapp: { icon: 'mdi:whatsapp', color: '#25D366' },
  phone: { icon: 'solar:phone-calling-rounded-bold-duotone', color: '#FCD34D' },
  sms: { icon: 'solar:chat-round-dots-bold-duotone', color: '#FCD34D' },
  linkedin: { icon: 'mdi:linkedin', color: '#38BDF8' },
};
